import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/cloudflare";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useNavigation,
  useRouteError,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import { Toaster } from "react-hot-toast";

import NProgress from "nprogress";
import "nprogress/nprogress.css";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { data } from "react-router";
import { AuthModal } from "~/components/auth/AuthModal";
import { BaseLayout } from "~/components/BaseLayout";
import { SessionRefreshModal } from "~/components/booking/SessionRefreshModal";
import { Footer } from "~/components/common/Footer";
import { SitePopup, type PopupConfig } from "~/components/common/SitePopup";
import { CookieBanner } from "~/components/CookieBanner";
import "~/globals.css";
import { fallbackLng } from '~/i18n';
import { ApiClient, OmniProduct } from "~/lib/api";
import { setRefreshModalCallback } from "~/lib/booking-api-interceptor";
import { bustDevCaches } from "~/lib/dev-cache.server";
import { getLocale } from '~/lib/i18n.server';
import { getScalapayBookingEnabled } from "~/lib/scalapay-booking.server";
import { getScalapayShopCheckoutVisibility } from "~/lib/scalapay-shop.server";
import { cleanupUrl, getMenuHref } from "~/lib/utils";
import { WooCommerceClient } from "~/lib/woocommerce";
import { getSessionStorage } from "~/sessions.server";
import { useAuth } from "~/stores/user";
import type { ShopProduct } from "~/types/shop";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Red+Hat+Display:ital,wght@0,300..900;1,300..900&family=Tenor+Sans&display=swap",
  },
  { rel: "icon", href: "/favicon.ico" },
  { rel: "apple-touch-icon", href: "/favicon.ico" },
];

export const meta: MetaFunction = () => {
  return [
    { title: "ChorusLife Immersive SPA – Centro benessere immersivo" },
    {
      name: "description",
      content:
        "Scopri ChorusLife Immersive SPA: un’esperienza di benessere totale con percorsi sensoriali, massaggi rilassanti e trattamenti personalizzati. Vivi il tuo momento di relax.",
    },
  ];
};

export async function loader({ request, context }: LoaderFunctionArgs) {
  const country = request.headers.get("cf-ipcountry");
  const pathname = new URL(request.url).pathname;
  const { getSession, commitSession } = getSessionStorage(
    context.cloudflare.env
  );
  const env: any = context?.cloudflare?.env;
  let footerData: any;
  const locale = await getLocale(request);
  const i18nDisabled = env?.I18N_DISABLED === "true";
  const lang = i18nDisabled ? fallbackLng : locale;
  await bustDevCaches(env);

  // Options and popup are read on every render: without a KV cache a traffic
  // spike becomes one WordPress request per page view, which took the CMS
  // down on 14 Sep 2026.
  const footerCacheKey = `wp_options_${lang}`;
  try {
    footerData = await env.KV.get(footerCacheKey, "json");
    if (!footerData) {
      const footerResp = await fetch(
        `${context.cloudflare.env.WP_API_HOST}wp-json/acf/v3/options/options?lang=${lang}`
      );
      footerData = await footerResp.json();
      if (footerResp.ok) {
        await env.KV.put(footerCacheKey, JSON.stringify(footerData), {
          expirationTtl: 300,
        });
      }
    }
  } catch (e) { }

  // Configurazione del popup sito (plugin chorus-wellness, endpoint REST).
  // Singola configurazione (non per-lingua); il frontend decide se/quando
  // mostrarlo in base a `enabled` e `frequency`.
  let popupConfig: PopupConfig | null = null;
  try {
    popupConfig = await env.KV.get("wp_popup", "json");
    if (!popupConfig) {
      const wpBase = (context.cloudflare.env.WP_API_HOST || "").replace(/\/+$/, "");
      const popupResp = await fetch(`${wpBase}/wp-json/chorus/v1/popup`);
      if (popupResp.ok) {
        popupConfig = await popupResp.json();
        await env.KV.put("wp_popup", JSON.stringify(popupConfig), {
          expirationTtl: 300,
        });
      }
    }
  } catch (e) {
    // configurazione popup opzionale: in caso di errore non mostriamo nulla
  }

  let servicesData;
  let serviceCategoriesData;

  const servicesCacheKey = `services_${lang}`;
  const serviceCategoriesCacheKey = `services_categories_${lang}`;

  const servicesFromCache = await env.KV.get(servicesCacheKey, "json");
  const serviceCategoriesFromCache = await env.KV.get(
    serviceCategoriesCacheKey,
    "json"
  );

  if (!serviceCategoriesFromCache) {
    const serviceCategoriesResp = await fetch(
      `${context.cloudflare.env.WP_API_HOST}wp-json/wp/v2/service_category?status=publish&lang=${lang}`
    );

    serviceCategoriesData = await serviceCategoriesResp.json();
    await env.KV.put(
      serviceCategoriesCacheKey,
      JSON.stringify(serviceCategoriesData),
      {
        expirationTtl: 1800,
      }
    );
  } else {
    serviceCategoriesData = serviceCategoriesFromCache;
  }

  const normalServiceCategories = serviceCategoriesData.filter(
    (category: any) =>
      !category.acf?.is_an_upselling && !category.acf?.hidden_on_booking
  );
  const upsellingServiceCategories = serviceCategoriesData.filter(
    (category: any) => category.acf?.is_an_upselling
  );

  if (!servicesFromCache) {
    const servicesResp = await fetch(
      `${context.cloudflare.env.WP_API_HOST}wp-json/acf/v3/servizio?status=publish&lang=${lang}`
    );
    servicesData = await servicesResp.json();

    // L'endpoint ACF non include la taxonomy: facciamo un secondo fetch
    // su /wp/v2/servizio per recuperare `service_category` per ogni post
    // e mergiamo via id. Così possiamo distinguere cmsService upselling
    // da ingressi/pacchetti senza nuovi campi ACF su WP.
    //
    // Dallo stesso fetch prendiamo anche `menu_order`: è il campo su cui il
    // plugin "Post Types Order" scrive l'ordine manuale del post type
    // `servizio`, e l'endpoint ACF (`acf/v3/servizio`, che restituisce solo
    // `{ id, acf }`) non lo espone. Serve alla pagina pubblica /servizi per
    // ordinare i servizi dentro la loro categoria — vedi
    // `buildServiceCatalog` in ~/lib/services-catalog.
    try {
      const servicesTaxResp = await fetch(
        `${context.cloudflare.env.WP_API_HOST}wp-json/wp/v2/servizio?status=publish&lang=${lang}&_fields=id,service_category,menu_order&per_page=100`
      );
      const servicesTax = await servicesTaxResp.json();
      if (Array.isArray(servicesData) && Array.isArray(servicesTax)) {
        const taxById = new Map<number, any>(
          servicesTax.map((s: any) => [s.id, s])
        );
        servicesData = servicesData.map((s: any) => ({
          ...s,
          service_category: taxById.get(s.id)?.service_category || [],
          menu_order: taxById.get(s.id)?.menu_order,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch service taxonomy:", error);
    }

    await env.KV.put(servicesCacheKey, JSON.stringify(servicesData), {
      expirationTtl: 1800,
    });
  } else {
    servicesData = servicesFromCache;
  }

  const session = await getSession(request.headers.get("Cookie"));

  const token = session.get("token");
  let enrollmentData: any;
  if (token) {
    const env: any = context?.cloudflare?.env;
    const bookingApiClient = new ApiClient(env);
    try {
      enrollmentData = await bookingApiClient.getEnrollmentData(token);
      enrollmentData = enrollmentData.data.data;
      session.set("customerId", enrollmentData.id);
    } catch (e: any) {
      console.error(
        "ERROR GETTING ENROLLMENT DATA",
        e?.response?.status,
        e?.response?.data
      );
    }
  }

  const bookingDisabled =
    context?.cloudflare?.env?.BOOKING_DISABLED === "true" ? true : false;

  const shopDisabled =
    context?.cloudflare?.env?.SHOP_DISABLED === "true" ? true : false;

  const voucherDisabled =
    context?.cloudflare?.env?.VOUCHER_DISABLED === "true" ? true : false;

  const giftCardsEnabled =
    context?.cloudflare?.env?.GIFT_CARDS_ENABLED === "true" ? true : false;

  // Gift card redemption gate. NOT the same flag as `giftCardsEnabled` above,
  // which only covers the shop voucher-gift customizer: this one mirrors
  // `BOOKING_GIFT_CARD_ENABLED`, the flag the server already enforces in
  // api.checkout.payment-data.ts and api.checkout.gift-card.tsx. It reaches
  // the client so the account tab and the cancellation refund code hide
  // themselves too, instead of advertising a feature the server refuses.
  // Safe default: OFF unless the env is explicitly "true".
  const bookingGiftCardEnabled =
    context?.cloudflare?.env?.BOOKING_GIFT_CARD_ENABLED === "true"
      ? true
      : false;

  // Scalapay promo widget config, shared by every placement (shop checkout,
  // booking checkout: the widget is shown at checkout only) via
  // useRouteLoaderData("root") —
  // see app/components/scalapay/ScalapayWidget.tsx. `getScalapayShopCheckoutVisibility`
  // resolves the min/max eligibility bounds with the same fallbacks as
  // app/lib/scalapay.ts's isAmountEligible (kept in sync there deliberately).
  const scalapayVisibility = getScalapayShopCheckoutVisibility(env);
  const scalapayMerchantToken = scalapayVisibility.enabled
    ? (env?.SCALAPAY_MERCHANT_TOKEN as string | undefined)
    : undefined;
  const scalapayApiBaseUrl = env?.SCALAPAY_API_BASE_URL as string | undefined;
  // Mirrors the sandbox-by-default posture documented in app/lib/scalapay.ts
  // (DEFAULT_API_BASE_URL points at the integration/sandbox host): treat a
  // missing or "integration"-containing base URL as sandbox, never guess
  // this client-side. A localhost base URL means the API is being pointed at
  // the local mock server (see tests/e2e/mocks/http-server.mjs), which is
  // likewise never production.
  const scalapaySandbox =
    !scalapayApiBaseUrl ||
    scalapayApiBaseUrl.includes("integration") ||
    scalapayApiBaseUrl.includes("localhost") ||
    scalapayApiBaseUrl.includes("127.0.0.1");

  // Booking-specific Scalapay gate. `SCALAPAY_MERCHANT_TOKEN` above is the
  // SHOP gate ONLY (it turns on the moment `SCALAPAY_ENABLED` is "true", via
  // `scalapayVisibility.enabled`) — a booking placement outside the checkout
  // must gate on this boolean too, or it re-creates the August 2026 bug where
  // the booking checkout widget advertised instalments while the booking
  // flag was off (see .claude/memory/scalapay-integration.md). The rule
  // itself is `isScalapayBookingEnabled` in `~/lib/scalapay`, which this
  // client-bundled module must not import directly: it reaches here through
  // the `.server.ts` wrapper, exactly like the shop visibility above.
  const scalapayBookingEnabled = getScalapayBookingEnabled(env);

  let shopProducts: ShopProduct[] = [];
  if (!shopDisabled && env?.WP_API_HOST && env?.WC_CONSUMER_KEY) {
    try {
      let productsFromCache = await env.KV.get("shop-products", "json");
      if (!productsFromCache || productsFromCache.length === 0) {
        const apiClient = new ApiClient(env);
        const response = await apiClient.getProducts();
        const productsArray = response.data.data || [];
        const activeProducts = productsArray.filter(
          (product: any) => product.Suspended === "No"
        );
        if (activeProducts.length > 0) {
          await env.KV.put("shop-products", JSON.stringify(activeProducts), {
            expirationTtl: 3600,
          });
          productsFromCache = activeProducts;
        }
      }
      const activeProductCodes = new Set(
        (productsFromCache || []).map((product: any) => product.Product)
      );
      const productsCacheMap = new Map<string, OmniProduct>(
        (productsFromCache || []).map((product: OmniProduct) => [
          product.Product,
          product,
        ])
      );
      const wooCommerceClient = new WooCommerceClient({
        WP_API_HOST: env.WP_API_HOST || "",
        WC_CONSUMER_KEY: env.WC_CONSUMER_KEY || "",
        WC_CONSUMER_SECRET: env.WC_CONSUMER_SECRET || "",
        I18N_DISABLED: env.I18N_DISABLED,
      });
      // Same reason as the options cache above: this ran on every render.
      const wooProductsCacheKey = `wc_products_${lang}`;
      let wooProducts: any[] | null = await env.KV.get(wooProductsCacheKey, "json");
      if (!wooProducts) {
        const productsResponse = await wooCommerceClient.getProducts({
          per_page: 100,
        }, lang);
        wooProducts = productsResponse.data || [];
        await env.KV.put(wooProductsCacheKey, JSON.stringify(wooProducts), {
          expirationTtl: 300,
        });
      }
      shopProducts = wooProducts
        .filter((product: any) => {
          const productCode = product.sku;
          if (!productCode || !activeProductCodes.has(productCode)) return false;
          const cachedProduct = productsCacheMap.get(productCode);
          if (!cachedProduct) return false;
          const promotionalPrice = cachedProduct.PromotionalPrice ?? 0;
          const standardPrice = cachedProduct.StandardPrice ?? 0;
          return promotionalPrice > 0 || standardPrice > 0;
        })
        .map((product: any) => {
          const productCode = product.sku;
          const cachedProduct = productsCacheMap.get(productCode);
          if (!cachedProduct) return null;
          const promotionalPrice = cachedProduct.PromotionalPrice ?? 0;
          const standardPrice = cachedProduct.StandardPrice ?? 0;
          const price =
            promotionalPrice > 0
              ? promotionalPrice
              : standardPrice > 0
                ? standardPrice
                : 0;
          return {
            ...product,
            price: price.toString(),
            regular_price: standardPrice.toString(),
            sale_price: promotionalPrice > 0 ? promotionalPrice.toString() : undefined,
            external_id: cachedProduct.Code,
            meta_data: [
              ...(product.meta_data || []),
              { id: Date.now(), key: "external_id", value: cachedProduct.Code },
            ],
          };
        })
        .filter((p: any) => p !== null);
    } catch (e) {
      console.error("Root loader: error loading shop products for vouchers", e);
    }
  }

  if (country && country !== "IT" && !i18nDisabled) {
    if (!pathname.includes("/en/")) {
      const cleanedPathname = cleanupUrl(pathname);
      return redirect(`/en${cleanedPathname}`);
    }
  }

  return data(
    {
      footerData,
      popup: popupConfig,
      enrollmentData,
      servicesData,
      serviceCategoriesData,
      normalServiceCategories,
      upsellingServiceCategories,
      shopProducts,
      token,
      GTM_ID: context?.cloudflare?.env?.GTM_ID,
      META_PIXEL_ID: context?.cloudflare?.env?.META_PIXEL_ID,
      SCALAPAY_MERCHANT_TOKEN: scalapayMerchantToken,
      SCALAPAY_SANDBOX: scalapaySandbox,
      SCALAPAY_MIN_AMOUNT: scalapayVisibility.min,
      SCALAPAY_MAX_AMOUNT: scalapayVisibility.max,
      SCALAPAY_BOOKING_ENABLED: scalapayBookingEnabled,
      bookingDisabled,
      shopDisabled,
      i18nDisabled,
      voucherDisabled,
      giftCardsEnabled,
      bookingGiftCardEnabled,
    },
    {
      headers: {
        "Set-Cookie": await commitSession(session),
      },
    }
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const locationPrev = useRef<string>("");
  const loaderData = useLoaderData<typeof loader>();
  const { login } = useAuth();
  const navigation = useNavigation();
  const [showRefreshModal, setShowRefreshModal] = useState(false);

  useEffect(() => {
    setRefreshModalCallback(setShowRefreshModal);
  }, []);

  useEffect(() => {
    if (navigation.state === "idle") {
      NProgress.done();
    } else {
      NProgress.start();
    }
  }, [navigation.state]);

  useEffect(() => {
    const cleanedCurrentPathname = cleanupUrl(location.pathname);
    const cleanedPrevCurrentPathname = cleanupUrl(locationPrev.current);
    if (
      location.hash === "" &&
      cleanedCurrentPathname !== cleanedPrevCurrentPathname
    )
      window.scrollTo({ top: 0, behavior: "instant" });

    locationPrev.current = location.pathname;
  }, [location]);

  useEffect(() => {
    if (loaderData?.enrollmentData && loaderData?.token) {
      login(loaderData?.token, loaderData?.enrollmentData);
    }
  }, [loaderData?.enrollmentData, loaderData?.token]);

  return (
    <html lang={i18n.language}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {loaderData?.GTM_ID && (
          <>
            <script
              suppressHydrationWarning
              dangerouslySetInnerHTML={{
                __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}
gtag('consent','default',{
  'ad_storage':'denied',
  'ad_user_data':'denied',
  'ad_personalization':'denied',
  'analytics_storage':'denied',
  'wait_for_update':500
});`,
              }}
            />
            <script
              suppressHydrationWarning
              dangerouslySetInnerHTML={{
                __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${loaderData?.GTM_ID}');`,
              }}
            />
          </>
        )}
        {loaderData?.META_PIXEL_ID && (
          <script
            suppressHydrationWarning
            dangerouslySetInnerHTML={{
              __html: `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${loaderData?.META_PIXEL_ID}');
fbq('track', 'PageView');`,
            }}
          />
        )}
        {/*
          Scalapay's widget loader is deliberately NOT server-rendered here.
          It injects its own nodes into <head> (its widget bundle and a Google
          Fonts stylesheet) and did so before React finished hydrating, which
          broke hydration on every page of the site while Scalapay was enabled.
          It is now injected from a client effect in
          app/components/scalapay/ScalapayWidget.tsx, which cannot race
          hydration and only loads it where a widget is actually rendered.
          SCALAPAY_MERCHANT_TOKEN is still surfaced by the loader below for
          that component to consume.
        */}
      </head>
      <body className="font-red-hat">
        {loaderData?.GTM_ID && (
          <noscript suppressHydrationWarning>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${loaderData?.GTM_ID}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            ></iframe>
          </noscript>
        )}
        {loaderData?.META_PIXEL_ID && (
          <noscript suppressHydrationWarning>
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              src={`https://www.facebook.com/tr?id=${loaderData?.META_PIXEL_ID}&ev=PageView&noscript=1`}
            />
          </noscript>
        )}
        {children}
        {loaderData && (
          <Footer
            footerData={loaderData?.footerData?.acf}
            bookingDisabled={loaderData?.bookingDisabled}
          />
        )}

        <Toaster position="top-right" />
        <CookieBanner />
        <AuthModal />
        <SitePopup config={loaderData?.popup} />
        <SessionRefreshModal
          open={showRefreshModal}
          onRefresh={() => {
            window.location.reload();
          }}
        />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function App() {
  return (
    <BaseLayout>
      <Outlet />
    </BaseLayout>
  );
}

const isLocal =
  typeof window !== "undefined"
    ? window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname.includes("localhost")
    : process.env.NODE_ENV === "development";

export default isLocal ? App : withSentry(App);

export function ErrorBoundary() {
  const error = useRouteError();
  const { t, i18n } = useTranslation();

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <html>
        <head>
          <title>404 - {t("Pagina non trovata")}</title>
          <Meta />
          <Links />
        </head>
        <body className="flex flex-col items-center justify-center pt-64">
          <h1 className="text-6xl font-bold">404</h1>
          <h5 className="text-2xl font-bold mt-1">{t("Pagina non trovata")}</h5>
          <p className="mt-8">{t("La pagina che stai cercando non esiste.")}</p>
          <a href={getMenuHref(i18n.language, "/")} className="mt-4 text-secondary underline">
            {t("Torna alla home")}
          </a>
          <Scripts />
        </body>
      </html>
    );
  }

  console.error("AN ERROR HERE", error);

  const isLocal =
    typeof window !== "undefined"
      ? window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.includes("localhost")
      : process.env.NODE_ENV === "development";

  if (!isLocal) {
    captureRemixErrorBoundaryError(error);
  }
  return (
    <html>
      <head>
        <title>500 - Server Error</title>
        <Meta />
        <Links />
      </head>
      <body className="flex flex-col items-center justify-center pt-64 w-full">
        <h1 className="text-6xl font-bold">500</h1>
        <h5 className="text-2xl font-bold mt-1">Server Error</h5>
        <p className="mt-8 pb-12">
          {t("Qualcosa è andato storto! Ricarica la pagina e riprova")}
        </p>
        <Scripts />
      </body>
    </html>
  );
}
